/**
 * The Phase 1 power-up pool — 06 §2, as DATA. Each entry is a pair of
 * tuning overlays; the sim never reads this file (it only sees
 * `player.tuning`, resolved from these overlays at RESET via
 * `resolveTuning`). Values are absolute per 06 §1.
 */
import type { TuningOverlay } from './tuning.js'

export interface PowerUpDef {
  id: string
  name: string
  /** 06 §5 silhouette name — the client maps it to a monochrome glyph. */
  glyph: 'bars' | 'triangle' | 'zigzag' | 'dashedRing' | 'halfDisc' | 'hook' | 'ringDot' | 'chevrons' | 'gapSquare'
  buff: TuningOverlay
  nerf: TuningOverlay
  blurb: string
  /** Reserved entries are not in the live pool. */
  reserved?: boolean
}

export const POWERUPS: readonly PowerUpDef[] = [
  { id: 'twin_disc', name: 'Twin Disc', glyph: 'bars', buff: { MAX_DISCS_HELD_BASE: 2 }, nerf: { DISC_THROW_SPEED: 20.4 }, blurb: 'Volume over velocity; crossfire solo.' },
  { id: 'heavy_disc', name: 'Heavy Disc', glyph: 'triangle', buff: { DISC_THROW_SPEED: 28.8, DISC_BOUNCE_RESTITUTION: 1.0 }, nerf: { DISC_MAX_BOUNCES: 1 }, blurb: 'The straight-shot specialist; one bank only.' },
  { id: 'banker', name: 'Banker', glyph: 'zigzag', buff: { DISC_BOUNCE_RESTITUTION: 1.08, DISC_MAX_BOUNCES: 5, DISC_RANGE: 60 }, nerf: { DISC_THROW_SPEED: 21.0 }, blurb: 'Discs gain speed per bounce; direct shots are weak.' },
  { id: 'ghost_step', name: 'Ghost Step', glyph: 'dashedRing', buff: { DASH_IFRAME_TICKS: 8 }, nerf: { DASH_COST: 50 }, blurb: 'Dash through discs; two dashes is a full bar.' },
  { id: 'bulwark', name: 'Bulwark', glyph: 'halfDisc', buff: { SHIELD_DRAIN: 15, SHIELD_ARC_HALF_DEG: 75, DEFLECT_LATE_COST: 10 }, nerf: { SHIELD_PERFECT_TICKS: 0 }, blurb: 'The wall. No riposte ever.' },
  { id: 'riposte', name: 'Riposte', glyph: 'hook', buff: { SHIELD_PERFECT_TICKS: 14, DEFLECT_PERFECT_SPEED_MULT: 1.3, DEFLECT_PERFECT_REFUND: 20 }, nerf: { SHIELD_RADIUS: 0.55, SHIELD_ARC_HALF_DEG: 40 }, blurb: 'Bigger parry window, stronger steal; a small shield you must place.' },
  { id: 'long_arm', name: 'Long Arm', glyph: 'ringDot', buff: { CATCH_RADIUS: 1.6, DISC_RETURN_SPEED: 22, DISC_RETURN_TURN_RATE: 900 }, nerf: { MOVE_SPEED: 5.85 }, blurb: 'Disc is back in hand fast; you are slow.' },
  { id: 'sprinter', name: 'Sprinter', glyph: 'chevrons', buff: { MOVE_SPEED: 7.48, STAMINA_REGEN: 35, STAMINA_REGEN_DELAY_TICKS: 24 }, nerf: { STAMINA_MAX: 70 }, blurb: 'Always moving, never turtling.' },
  { id: 'intercept', name: 'Intercept', glyph: 'gapSquare', buff: { INTERCEPT_ENABLED: true }, nerf: { CATCH_RADIUS: 0.6 }, blurb: 'Reserved: catch enemy returns through a raised shield.', reserved: true },
]

export const POWERUP_POOL: readonly PowerUpDef[] = POWERUPS.filter((p) => !p.reserved)
export const POWERUP_IDS: readonly string[] = POWERUP_POOL.map((p) => p.id)
/** 06 §4 default deck when a player picks fewer than 3. */
export const DEFAULT_DECK: readonly string[] = ['heavy_disc', 'bulwark', 'sprinter']

export function powerUpById(id: string): PowerUpDef | undefined {
  return POWERUPS.find((p) => p.id === id)
}

/** Overlays for a list of power-up ids (unknown ids ignored). Order matters
 *  for the first-wins disjoint-key rule (12 §8.1). */
export function overlaysFor(ids: readonly string[]): TuningOverlay[] {
  const out: TuningOverlay[] = []
  for (const id of ids) {
    const p = powerUpById(id)
    if (p) out.push({ ...p.buff, ...p.nerf })
  }
  return out
}
